import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { Building2, Mail, Lock, Globe, Eye, EyeOff, ArrowLeft, ArrowRight } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { t, language, toggleLanguage } = useLanguage();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const result = await login(email, password);
    
    if (result.success) {
      navigate(result.user?.role === 'admin' ? '/admin' : '/dashboard');
    } else {
      setError(result.error);
    }
    
    setLoading(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-purple-50/20 flex items-center justify-center px-6" data-testid="login-page">
      <div className="max-w-md w-full">
        {/* Top Bar */}
        <div className="flex justify-between items-center mb-4">
          <Link to="/" className="flex items-center gap-1.5 text-sm font-medium text-muted-foreground hover:text-primary transition-colors" data-testid="back-to-home">
            {language === 'ar' ? <ArrowRight size={18} /> : <ArrowLeft size={18} />}
            {language === 'ar' ? 'الصفحة الرئيسية' : 'Home'}
          </Link>
          <button
            onClick={toggleLanguage}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-muted-foreground hover:text-primary transition-colors bg-white rounded-lg border border-border"
            data-testid="language-toggle"
          >
            <Globe size={18} />
            {language === 'ar' ? 'English' : 'عربي'}
          </button>
        </div>

        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2 mb-6">
            <div className="w-12 h-12 bg-gradient-to-br from-accent to-accent/70 rounded-xl flex items-center justify-center">
              <Building2 size={28} className="text-white" />
            </div>
            <span className="text-3xl font-heading font-black bg-gradient-to-r from-primary to-accent bg-clip-text text-transparent">{t('appName')}</span>
          </Link>
          <h1 className="text-3xl font-heading font-bold text-primary mb-2">{t('login')}</h1>
          <p className="text-muted-foreground">{t('welcomeBack')}</p>
        </div>

        <div className="bg-white p-8 rounded-2xl border border-border shadow-lg">
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('email')}
              </label>
              <div className="relative">
                <Mail size={20} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="email-input"
                  className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10' : 'pl-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent`}
                  placeholder="example@company.com"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-foreground mb-2">
                {t('password')}
              </label>
              <div className="relative">
                <Lock size={20} className={`absolute ${language === 'ar' ? 'right-3' : 'left-3'} top-1/2 -translate-y-1/2 text-muted-foreground`} />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="password-input"
                  className={`w-full px-4 py-3 ${language === 'ar' ? 'pr-10 pl-10' : 'pl-10 pr-10'} border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-accent`}
                  placeholder="••••••••"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  data-testid="toggle-password-visibility"
                  className={`absolute ${language === 'ar' ? 'left-3' : 'right-3'} top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors`}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg" data-testid="error-message">
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            <div className="flex justify-end">
              <Link to="/forgot-password" className="text-sm text-accent hover:underline font-medium" data-testid="forgot-password-link">
                {language === 'ar' ? 'نسيت كلمة السر؟' : 'Forgot password?'}
              </Link>
            </div>

            <button
              type="submit"
              disabled={loading}
              data-testid="login-submit-btn"
              className="w-full py-3 bg-gradient-to-r from-accent to-accent/90 text-white font-semibold rounded-lg hover:shadow-lg hover:-translate-y-0.5 disabled:opacity-50 transition-all duration-200"
            >
              {loading ? t('loggingIn') : t('login')}
            </button>
          </form>

          <div className="mt-6 text-center">
            <p className="text-muted-foreground">
              {t('noAccount')}{' '}
              <Link to="/register" className="text-accent hover:underline font-medium" data-testid="register-link-from-login">
                {t('registerNow')}
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}